import React, { useEffect, useMemo, useRef, useState } from 'react';
import '../App.css';
import { fetchDeliveryJson } from '../utils/deliveryApiFetch';
import { matchesSearch } from '../ros/rosActivity';
import { formatLoadInDate, formatShortDate, formatShortRange, projectsNearLoadIn } from '../ros/loadInWindow';
import { pickKey } from '../ros/members';

const MIN_PROJECTS = 2;
const NEARBY_SHOWN = 4;
const EXISTING_SHOWN = 5;

/** One project, read as an event line: name, then date · venue, then quiet IDs. */
function EventLine({ c, action, note }) {
  const meta = [c.projectId ? `Project #${c.projectId}` : 'No Project ID', c.programId].filter(Boolean).join(' · ');
  return (
    <div className="me-event">
      <div className="me-event-main">
        <div className="me-event-title">{c.title}</div>
        <div className="me-event-sub">{[c.dateText, c.venueName].filter(Boolean).join(' · ')}</div>
        <div className="me-event-meta">{meta}</div>
        {note}
      </div>
      {action && <div className="me-event-action">{action}</div>}
    </div>
  );
}

/**
 * 02 — Select Projects for a new Multi-Event packet: search for the first
 * project, then pick from projects loading in the same week, or search again.
 * Choosing projects writes nothing: the parent starts the editor with the
 * selection and the first save creates the packet.
 */
/** "Nov 4–13, 2026 · Charleston, SC · 2 projects" for one packet. */
export function packetMeta(p) {
  const s = p.summary || {};
  const count = s.projectCount || (p.memberIds || []).length;
  return [formatShortRange(s.startISO, s.endISO), s.cityState, `${count} project${count === 1 ? '' : 's'}`].filter(Boolean).join(' · ');
}

/**
 * Current packets first (by first project date), then past ones. Current =
 * the packet's last project date has not passed.
 */
export function orderPackets(packets) {
  const key = (p) => (p.summary && p.summary.startISO) || '9999';
  const cur = (packets || []).filter((p) => !p.summary || p.summary.current).sort((a, b) => (key(a) < key(b) ? -1 : key(a) > key(b) ? 1 : 0));
  const past = (packets || []).filter((p) => p.summary && !p.summary.current).sort((a, b) => (key(a) > key(b) ? -1 : key(a) < key(b) ? 1 : 0));
  return { current: cur, past };
}

/**
 * Where the selected projects already are: when every one is in the same
 * current packet, that packet (the coordinator most likely wants to open it,
 * and building would only make a second packet for the same projects).
 */
export function selectionPackets(selected, candidatesById, packetsById) {
  const of = (pid) => ((candidatesById[pid] || {}).inPackets || [])
    .map((id) => packetsById[id]).filter((p) => p && (!p.summary || p.summary.current));
  const per = selected.map((pid) => ({ projectId: pid, packets: of(pid) }));
  const shared = selected.length
    ? of(selected[0]).filter((p) => per.every((x) => x.packets.some((q) => q.rosPacketId === p.rosPacketId)))
    : [];
  return { per, same: shared[0] || null };
}

function MultiEventPicker({ deliveryApiUrl, initialSelection, starting, startError, onStart, onOpenPacket }) {
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(initialSelection || []);
  const [nearbyOpen, setNearbyOpen] = useState(false);

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const res = await fetchDeliveryJson(`${deliveryApiUrl}?action=getRosPicker`, undefined, { retries: 2 });
        if (!live) return;
        if (res && res.success) setData(res);
        else setLoadError((res && res.error) || 'Could not load EPS projects.');
      } catch (err) {
        if (live) setLoadError(err.message || 'Network error');
      }
    })();
    return () => { live = false; };
  }, [deliveryApiUrl]);

  const candidates = useMemo(() => (data && data.candidates) || [], [data]);
  const byId = useMemo(() => {
    const m = {};
    // An EPS row without a Project ID is selected by its row (EPS-only event).
    candidates.forEach((c) => { if (c.available) m[pickKey(c)] = c; });
    return m;
  }, [candidates]);
  const packetsById = useMemo(() => {
    const m = {};
    ((data && data.packets) || []).forEach((p) => { m[p.rosPacketId] = p; });
    return m;
  }, [data]);

  const add = (pid) => setSelected((s) => (s.indexOf(pid) === -1 ? [...s, pid] : s));
  // Adding a search result finishes that search: clear it, ready for the next.
  const searchRef = useRef(null);
  const addFromSearch = (pid) => {
    add(pid);
    setQuery('');
    if (searchRef.current) searchRef.current.focus();
  };
  const remove = (pid) => setSelected((s) => s.filter((x) => x !== pid));

  // Suggestions follow the first selected project's Load-In date; later
  // selections never move the window.
  const anchor = selected.length ? byId[selected[0]] : null;
  const nearby = anchor ? projectsNearLoadIn(candidates, anchor.loadInISO, selected) : [];
  const anchorId = anchor ? anchor.projectId : '';
  useEffect(() => { setNearbyOpen(false); }, [anchorId]);
  const nearbyShown = nearbyOpen ? nearby : nearby.slice(0, NEARBY_SHOWN);
  const nearbyHidden = nearby.length - NEARBY_SHOWN;
  const nearbyRows = new Set(nearby.map((c) => c.rowNumber));

  // Other members of a selected project's Program that the week list does
  // not already show. Never added automatically.
  const programIds = Array.from(new Set(selected.map((pid) => (byId[pid] || {}).programId).filter(Boolean)));
  const programGroups = programIds.map((prg) => ({
    programId: prg,
    programName: (candidates.find((c) => c.programId === prg && c.programName) || {}).programName || '',
    items: candidates.filter((c) => c.programId === prg && c.available && selected.indexOf(pickKey(c)) === -1 &&
      !nearbyRows.has(c.rowNumber) && !/cancel/i.test(c.proposalStatus || ''))
  })).filter((g) => g.items.length);

  const results = query.trim() ? candidates.filter((c) => matchesSearch(c, query)).slice(0, 30) : [];
  const tooFew = selected.length < MIN_PROJECTS;
  const [allPacketsOpen, setAllPacketsOpen] = useState(false);
  const ordered = orderPackets((data && data.packets) || []);
  const where = selectionPackets(selected, byId, packetsById);
  const others = where.same ? [] : where.per.filter((x) => x.packets.length);

  const openPacket = (p) => { if (p && p.identity && onOpenPacket) onOpenPacket(p); };
  const openAction = (p, label) => (p.identity
    ? <button type="button" className="me-action me-action--strong" onClick={() => openPacket(p)}>{label}</button>
    : p.latestPacketUrl
      ? <a className="me-action" href={p.latestPacketUrl} target="_blank" rel="noopener noreferrer">View latest PDF</a>
      : null);
  // Context, not an error: which current Multi-Event packet a project is already in.
  const membership = (c) => {
    const list = ((c && c.inPackets) || []).map((id) => packetsById[id]).filter((p) => p && (!p.summary || p.summary.current));
    return list.map((p) => (
      <div key={p.rosPacketId} className="me-membership">
        <span className="me-meta">In Multi-Event Packet: {p.title}</span>
        {p.identity && <><span className="me-meta" aria-hidden="true">·</span>{openAction(p, 'Open →')}</>}
      </div>
    ));
  };

  const candidateRow = (c, key, onAdd = add) => {
    const isSelected = selected.indexOf(pickKey(c)) !== -1 && c.available;
    return (
      <li key={key} className={`me-row${c.available ? '' : ' me-row--off'}`}>
        <EventLine
          c={c}
          note={
            <>
              {!c.available && <div className="me-event-note">{c.reason}</div>}
              {membership(c)}
            </>
          }
          action={
            isSelected ? <span className="me-event-meta">Added</span>
              : c.available ? <button type="button" className="pp-link-btn" onClick={() => onAdd(pickKey(c))}>Add</button>
                : null
          }
        />
      </li>
    );
  };

  // Nearby suggestions: one light line each — Load-In · name · Project #, venue
  // second; ineligible rows keep their reason and have no Add.
  const nearbyRow = (c) => (
    <li key={`w-${c.rowNumber}`} className={`me-nearby-row${c.available ? '' : ' me-row--off'}`}>
      <div className="me-nearby-main">
        <div className="me-nearby-line">
          <span className="me-nearby-date">{formatShortDate(c.loadInISO)}</span>
          <span className="me-nearby-sep"> · </span>
          <span className="me-nearby-title">{c.title}</span>
          <span className="me-nearby-sep"> · </span>
          <span className="me-nearby-id">{c.projectId ? `#${c.projectId}` : 'No Project ID'}</span>
        </div>
        {c.venueName && <div className="me-nearby-venue">{c.venueName}</div>}
        {!c.available && <div className="me-event-note">{c.reason}</div>}
        {membership(c)}
      </div>
      {c.available && (
        <button type="button" className="pp-link-btn" onClick={() => add(pickKey(c))}
          aria-label={`Add ${c.title}${c.projectId ? ` (#${c.projectId})` : ''}`}>Add</button>
      )}
    </li>
  );

  return (
    <div className="me-picker">
      {selected.length > 0 && (
        <div className="me-block">
          <div className="me-control-label">Selected projects · {selected.length}</div>
          <ol className="me-list me-list--selected">
            {selected.map((pid) => {
              const c = byId[pid] || { projectId: /^row:/.test(pid) ? '' : pid, title: /^row:/.test(pid) ? 'EPS event' : `Project #${pid}`, dateText: 'Not found on the EPS' };
              return (
                <li key={pid} className="me-row">
                  <EventLine
                    c={c}
                    note={<>
                      {/* An EPS-only event has no project resources, so its folder note would not help. */}
                      {c.hasDriveFolder === false && !c.epsOnly && <div className="me-event-note">No Drive folder listed in EPS. Add one to include this project’s floor plans.</div>}
                      {membership(c)}
                    </>}
                    action={<button type="button" className="pp-link-btn" onClick={() => remove(pid)}>Remove</button>}
                  />
                </li>
              );
            })}
          </ol>
        </div>
      )}

      {/* Every selected project is already in one packet: open it rather than build a second. */}
      {!tooFew && where.same && (
        <div className="me-same-packet" role="status">
          <div className="me-value">These projects are already part of the same Multi-Event packet.</div>
          <div className="me-same-title">{where.same.title}</div>
          <div className="me-meta">{packetMeta(where.same)}</div>
          <div className="me-same-actions">
            {where.same.identity ? (
              <button type="button" className="btn-calculate pp-btn-emphasis" onClick={() => openPacket(where.same)}>Open Multi-Event Packet →</button>
            ) : openAction(where.same, '')}
            <span className="helper-text">To build a different packet, add a project that isn’t in it.</span>
          </div>
        </div>
      )}

      <div className="me-block">
        <label className="me-control-label" htmlFor="me-project-search">Search projects</label>
        <input
          id="me-project-search"
          ref={searchRef}
          type="search"
          className="input-field"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by client, venue, project #, or event date"
          disabled={!data}
        />
        {!data && !loadError && <p className="helper-text me-empty">Loading projects from the Event Production Sheet…</p>}
        {loadError && <p className="helper-text pp-error">{loadError}</p>}
        {data && query.trim() && (
          <ul className="me-list me-results">
            {results.length === 0 && <li className="helper-text me-empty">No matching EPS projects.</li>}
            {results.map((c) => candidateRow(c, `r-${c.rowNumber}`, addFromSearch))}
          </ul>
        )}
      </div>

      {anchor && anchor.loadInISO && (
        <div className="me-block me-nearby">
          <div className="me-control-label">Nearby projects{nearby.length ? ` · ${nearby.length} found` : ''}</div>
          <p className="helper-text me-block-intro">
            Other projects with load-in dates within 7 days of {formatLoadInDate(anchor.loadInISO)}.
          </p>
          {nearby.length === 0 ? (
            <p className="helper-text me-empty">No other projects load in within 7 days.</p>
          ) : (
            <ul className="me-nearby-list">{nearbyShown.map(nearbyRow)}</ul>
          )}
          {nearbyHidden > 0 && (
            <button type="button" className="pp-link-btn me-nearby-toggle" aria-expanded={nearbyOpen}
              onClick={() => setNearbyOpen((o) => !o)}>
              {nearbyOpen ? 'Show fewer' : `Show ${nearbyHidden} more`}
            </button>
          )}
        </div>
      )}

      {programGroups.map((g) => (
        <div key={g.programId} className="me-block">
          <div className="me-control-label">
            Also in {g.programId}{g.programName ? ` · ${g.programName}` : ''}
          </div>
          <ul className="me-list">
            {g.items.map((c) => candidateRow(c, `s-${c.rowNumber}`))}
          </ul>
        </div>
      ))}

      {!(!tooFew && where.same) && (
        <>
          {!tooFew && others.length > 0 && (
            <div className="me-overlap" role="status">
              <div className="me-meta">Some selected projects are already in other Multi-Event packets. Building adds them to this new packet too; their current packets are not changed.</div>
              {others.map((x) => (
                <div key={x.projectId} className="me-membership">
                  <span className="me-meta">{(byId[x.projectId] || { title: `Project #${x.projectId}` }).title} — in {x.packets.map((p) => p.title).join(', ')}</span>
                  {x.packets[0].identity && <><span className="me-meta" aria-hidden="true">·</span>{openAction(x.packets[0], 'Open →')}</>}
                </div>
              ))}
            </div>
          )}
          <div className="me-footer">
            {/* Guidance only when it helps: one project chosen, one more needed. */}
            <span className="helper-text">{selected.length === 1 ? 'Add at least one more project.' : ''}</span>
            <button type="button" className="btn-calculate pp-btn-emphasis"
              disabled={tooFew || starting} onClick={() => onStart(selected)}>
              {starting ? (<><span className="spinner-small"></span><span style={{ marginLeft: 8 }}>Preparing…</span></>) : 'Build Multi-Event Packet →'}
            </button>
          </div>
        </>
      )}
      {startError && <div className="pp-folder-warning" role="status">{startError}</div>}

      {/* Secondary path: packets already started, current ones first. */}
      <div className="me-existing">
        <div className="me-label">Existing Multi-Event packets</div>
        <p className="helper-text me-block-intro">Continue a packet you’ve already started.</p>
        {!data && !loadError && <p className="helper-text me-empty">Loading…</p>}
        {data && ordered.current.length === 0 && ordered.past.length === 0 && <p className="helper-text me-empty">No Multi-Event packets yet.</p>}
        {data && ordered.current.length === 0 && ordered.past.length > 0 && !allPacketsOpen && (
          <p className="helper-text me-empty">No current or upcoming Multi-Event packets.</p>
        )}
        <ul className="me-existing-list">
          {(allPacketsOpen ? ordered.current.concat(ordered.past) : ordered.current.slice(0, EXISTING_SHOWN)).map((p) => (
            <li key={p.rosPacketId} className="me-existing-row">
              <div className="me-existing-main">
                <div className="me-existing-title">{p.title}</div>
                <div className="me-meta">{packetMeta(p)}{p.summary && !p.summary.current ? ' · Past' : ''}</div>
              </div>
              <div className="me-existing-action">{openAction(p, 'Open packet →')}</div>
            </li>
          ))}
        </ul>
        {(ordered.current.length > EXISTING_SHOWN || ordered.past.length > 0) && (
          <button type="button" className="me-action" aria-expanded={allPacketsOpen} onClick={() => setAllPacketsOpen((o) => !o)}>
            {allPacketsOpen ? 'Show current packets only' : `View all Multi-Event packets (${ordered.current.length + ordered.past.length}) →`}
          </button>
        )}
      </div>
    </div>
  );
}

export default MultiEventPicker;
